/*
  assets/artisan-filtre-cafe.js
  Filters an already-rendered collection grid by intensity, bitterness and
  family, with three taste shortcuts that set the intensity in one click.

  The cards are the theme's own, untouched — this only decides which of them
  are displayed. Each card is matched to its metafield values through the
  product handle in its link, so nothing has to be threaded through the card
  snippet and the grid keeps working if this script never loads.

  Filters are additive per axis: picking 3 and 4 on intensity means "3 or 4",
  because someone who likes a medium roast is choosing a range, not a value.
  Across axes they intersect. A shortcut (Doux / Équilibré / Corsé on
  intensity, Aucune / Peu / Assez bien on bitterness) is just a range of
  values pressed at once; on intensity, pressing an individual value
  afterwards releases the shortcut. Bitterness has no grid: the shortcuts
  are its only handles.
*/

class ArtisanFiltreCafe extends HTMLElement {
  connectedCallback() {
    try {
      this.map = JSON.parse(this.dataset.map || '{}');
    } catch (e) {
      return;
    }

    this.grid = document.querySelector(this.dataset.target);
    if (!this.grid) return;

    this.total = parseInt(this.dataset.total, 10) || 0;
    // Les notes seules : les raccourcis portent aussi data-axis, mais pas de
    // valeur, et ils ont leur propre gestion.
    this.pills = Array.from(this.querySelectorAll('[data-axis][data-value]'));
    this.raccourcis = Array.from(this.querySelectorAll('[data-raccourci]'));
    this.familles = Array.from(this.querySelectorAll('[data-famille]'));
    this.countEl = this.querySelector('[data-flt-count]');
    this.resetEl = this.querySelector('[data-flt-reset]');
    this.selected = { i: new Set(), a: new Set() };
    this.famille = '';

    this.cards = this.collectCards();
    if (!this.cards.length) return;

    this.pills.forEach((pill) => pill.addEventListener('click', () => this.toggle(pill)));
    this.raccourcis.forEach((r) => r.addEventListener('click', () => this.raccourci(r)));
    this.familles.forEach((f) => f.addEventListener('click', () => this.choisirFamille(f)));
    this.resetEl?.addEventListener('click', () => this.reset());

    // Wording comes from the section settings so it can be translated; the
    // French here is only the fallback.
    this.txt = {
      aucun: this.dataset.texteAucun || 'Aucun café ne correspond — élargissez votre choix.',
      unite: (this.dataset.unite || 'café|cafés').split('|'),
      sur: this.dataset.sur || 'sur',
    };

    this.apply();
  }

  /* One entry per card: the element to hide, and the handle that keys the map.
     Read once — the grid does not change under us. */
  collectCards() {
    const links = Array.from(this.grid.querySelectorAll('a[href*="/products/"]'));
    const seen = new Set();
    const cards = [];

    links.forEach((link) => {
      const match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!match) return;
      const handle = match[1];

      // The item wrapper, not the link: hiding the link alone would leave the
      // grid cell behind as a gap.
      const item = link.closest('.grid__item, .tab__cell, li');
      if (!item || seen.has(item)) return;
      seen.add(item);
      cards.push({ item, handle });
    });

    return cards;
  }

  toggle(pill) {
    const axis = pill.dataset.axis;
    const value = parseInt(pill.dataset.value, 10);
    const set = this.selected[axis];

    if (set.has(value)) {
      set.delete(value);
      pill.setAttribute('aria-pressed', 'false');
    } else {
      set.add(value);
      pill.setAttribute('aria-pressed', 'true');
    }
    // A hand-picked value is no longer one of the presets on that axis.
    this.raccourcis
      .filter((r) => r.dataset.axis === axis)
      .forEach((r) => r.setAttribute('aria-pressed', 'false'));

    this.apply();
  }

  /* A shortcut presses a whole range of values on its axis. Pressing the
     active shortcut again releases everything on that axis. */
  raccourci(bouton) {
    const axis = bouton.dataset.axis || 'i';
    const set = this.selected[axis];
    const actif = bouton.getAttribute('aria-pressed') === 'true';
    set.clear();
    this.raccourcis
      .filter((r) => r.dataset.axis === axis)
      .forEach((r) => r.setAttribute('aria-pressed', 'false'));
    if (!actif) {
      const min = parseInt(bouton.dataset.min, 10);
      const max = parseInt(bouton.dataset.max, 10);
      for (let v = min; v <= max; v += 1) set.add(v);
      bouton.setAttribute('aria-pressed', 'true');
    }
    this.pills
      .filter((p) => p.dataset.axis === axis)
      .forEach((p) => p.setAttribute('aria-pressed', set.has(parseInt(p.dataset.value, 10)) ? 'true' : 'false'));
    this.apply();
  }

  choisirFamille(bouton) {
    this.famille = bouton.dataset.famille || '';
    this.familles.forEach((f) => f.setAttribute('aria-pressed', f === bouton ? 'true' : 'false'));
    this.apply();
  }

  reset() {
    this.selected.i.clear();
    this.selected.a.clear();
    this.famille = '';
    this.pills.forEach((p) => p.setAttribute('aria-pressed', 'false'));
    this.raccourcis.forEach((r) => r.setAttribute('aria-pressed', 'false'));
    this.familles.forEach((f) => f.setAttribute('aria-pressed', f.dataset.famille === '' ? 'true' : 'false'));
    this.apply();
  }

  filtering() {
    return this.selected.i.size || this.selected.a.size || this.famille !== '';
  }

  matches(handle) {
    const values = this.map[handle];
    if (!this.filtering()) return true;
    if (!values) return false;
    if (this.famille && values.f !== this.famille) return false;
    // An unrated coffee cannot satisfy a scale filter. A missing value is
    // published as null, never 0: zero bitterness is a real rating.
    if (this.selected.i.size && (values.i === null || !this.selected.i.has(values.i))) return false;
    if (this.selected.a.size && (values.a === null || !this.selected.a.has(values.a))) return false;
    return true;
  }

  plural(n) {
    return `${n} ${this.txt.unite[n > 1 ? 1 : 0] || this.txt.unite[0]}`;
  }

  apply() {
    let shown = 0;

    this.cards.forEach(({ item, handle }) => {
      const visible = this.matches(handle);
      item.hidden = !visible;
      if (visible) shown += 1;
    });

    const filtering = this.filtering();
    if (this.resetEl) this.resetEl.hidden = !filtering;

    if (this.countEl) {
      if (!filtering) {
        this.countEl.textContent = this.plural(this.total);
      } else if (shown === 0) {
        this.countEl.textContent = this.txt.aucun;
      } else {
        this.countEl.textContent = `${this.plural(shown)} ${this.txt.sur} ${this.total}`;
      }
    }
  }
}

if (!customElements.get('artisan-filtre-cafe')) {
  customElements.define('artisan-filtre-cafe', ArtisanFiltreCafe);
}
